<template>
  <div class="content">
    <Background>
      <Loading v-if="loading" isCenter isFull />
      <FriendlierCard v-else>
        <div class="padded-block">
          <div class="logo">
            <LocaleAwareLogo variant="primary" color="cloud" />
          </div>

          <div v-if="success" class="success-section">
            <h1 class="success-title is-cloud">
              {{ supportsQuickMoney ? t("claimContainer.successful-request") : t("claimContainer.successful-claim") }}
            </h1>
            <p class="success-detail is-cloud">
              {{
                supportsQuickMoney
                  ? t("claimContainer.successful-request-detail")
                  : t("claimContainer.successful-claim-detail")
              }}
            </p>
          </div>
          <div v-else>
            <!-- Step 1: Container ID Input (if no code in URL) -->
            <div v-if="!supportsQuickMoney && !showFastlaneClaim" class="container-input-section">

              <div class="input-container">
                <div class="label">Container Number:</div>
                <input class="input" v-model="containerID" type="text" placeholder="CXXXXXX" @input="containerChange" />
              </div>
              <div class="input-with-icon">
                <div class="label">Container PIN:</div>
                <input class="input" type="text" placeholder="if present" v-model="pin" maxlength="3"
                  inputmode="numeric" autocomplete="off" id="pin_input" :disabled="claimLoading" />
                <span class="info-icon" @click="showPinTooltip = !showPinTooltip">i</span>
              </div>
              <div v-if="showPinTooltip" class="pin-tooltip">
                {{ t('claimContainer.new-pin') }}
              </div>
            </div>

            <!-- Step 2: Action Buttons (after container is loaded) -->
            <div class="action-buttons-section">
              <div class="title">Choose your action</div>

              <!-- Email Input -->
              <div class="email-input-container">
                <div class="label">Email:</div>
                <div class="input-wrapper">
                  <input class="input" v-model="userEmail" id="return-email" name="email" type="email"
                    placeholder="optional for donations" autocomplete="email" inputmode="email" autocapitalize="off"
                    spellcheck="false" @input="emailChange" @blur="emailBlur" :disabled="claimLoading" />
                  <p v-if="emailSuggestion" class="email-suggestion">
                    Did you mean
                    <button type="button" class="email-suggestion-link" @click="applyEmailSuggestion">{{ emailSuggestion
                    }}</button>?
                  </p>
                </div>
              </div>
              <div class="error">{{ error }}</div>

              <!-- Quick Money Button (only if outlet supports it) -->
              <div v-if="supportsQuickMoney" class="button-container">
                <button class="button primary-button" @click="handleQuickMoney" :disabled="!userEmail || claimLoading">
                  {{ t("claimContainer.claim-deposit") }}
                </button>
              </div>
              <!-- Fastlane claim by URL -->
              <div v-else-if="showFastlaneClaim" class="button-container">
                <button class="button primary-button" @click="handleFastlaneClaim"
                  :disabled="claimLoading || !userEmail">
                  <Loading v-if="claimLoading" isCenter isContained />
                  <span v-else>{{ t("claimContainer.claim-deposit") }}</span>
                </button>
              </div>
              <!-- Manually entered claim with PIN -->
              <div v-else class="button-container">
                <button class="button primary-button" @click="handleStandardClaim"
                  :disabled="claimLoading || !userEmail">
                  <Loading v-if="claimLoading" isCenter isContained />
                  <span v-else>{{ t("claimContainer.claim-deposit") }}</span>
                </button>
              </div>
              <!-- Quick Donate Button (always visible) -->
              <div class="button-container">
                <button class="button secondary-button" @click="handleQuickDonate"
                  :disabled="claimLoading || (!containerID && !scannedUrl)">
                  {{ t("claimContainer.quick-donate") }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Next Steps Section -->
        <div :class="{ 'image-spacer': true, 'extra-space': showPinTooltip }"
          :style="{ backgroundImage: 'url(' + (showPinTooltip ? CodeDetails : FourContainerSushiFirst) + ')' }" />

        <div class="padded-block">
          <div class="title is-center semi-bold">{{ t("quickReturn.howItWorks") }}</div>

          <div class="detail-card" v-for="detail in details" :key="detail.title">
            <div>{{ detail.title }}</div>
            <div><img :src="detail.image" /></div>
            <p v-if="detail.title !== 'deposit'">{{ detail.text }}</p>
            <p v-else style="text-align: left">
              Once you receive a notification that your deposit has been processed, you can either:
              <br />
              1. Request a payment

              <br />2. Donate to a local charity Visit friendlier.com for a list of charities.
            </p>

            <span v-if="detail.buttonText" class="button">
              <a target="_blank" :href="detail.buttonLink" @click="detail.buttonClick">
                {{ detail.buttonText }}
              </a>
            </span>

            <span class="divider" />
          </div>

          <div class="sign-off">{{ t("quickReturn.cta") }}</div>

          <div class="button-row">
            <div class="button big-button">
              <a :href="'https://' + t('quickReturn.url')" target="_blank"> {{ t('quickReturn.url') }} </a>
            </div>
          </div>

          <div class="foot">
            <a href="https://www.friendlier.com/general-5" target="_blank">
              <span>{{ t("quickReturn.FAQ") }}</span>
            </a>

            <span class="divider">|</span>

            <a href="https://www.friendlier.com/privacy-policy" target="_blank">
              <span>{{ t("quickReturn.PP") }}</span>
            </a>

            <span class="divider">|</span>

            <a href="https://www.friendlier.com/terms-of-service" target="_blank">
              <span>{{ t("quickReturn.TOS") }}</span>
            </a>
          </div>
          <div class="copyright">
            {{ t("quickReturn.copyright", { year: new Date().getFullYear() }) }}
          </div>
        </div>
      </FriendlierCard>
      <LocaleSwitcher />
    </Background>
  </div>
</template>

<script setup>
import { onMounted, ref, watch, computed } from "vue";
import { useRouter, useRoute } from "vue-router";

import { FourContainerSushiFirst, CodeDetails } from "@/revamp/assets/images";
import { LineDropOffBinCloud, MoneyWithArrowCloud, LineEarthCloud, LineContainersCloud } from "@/revamp/assets/images";
import { Loading, Background, FriendlierCard, LocaleAwareLogo, LocaleSwitcher } from "@/revamp/components";
import { notifyStore } from "@/revamp/store";
import { useFirebase } from "@/revamp/composables";
import { AuthService } from "@/revamp/firebase/config";
import { getSuggestedEmail, logger, isMobile, toQueryString } from "@/revamp/utils";

import { useI18n } from "vue-i18n";

const containerID = ref("");
const pin = ref("");
const userEmail = ref("");
const emailSuggestion = ref(null);
const scannedUrl = ref("");
const showFastlaneClaim = ref(false);

const error = ref("");
const loading = ref(true);
const claimLoading = ref(false);
const success = ref(false);
const supportsQuickMoney = ref(false);
const showPinTooltip = ref(false);

const APIService = useFirebase();
const Router = useRouter();
const route = useRoute();
const { t } = useI18n();

onMounted(async () => {
  try {
    scannedUrl.value = toQueryString(route.query.url);
    userEmail.value = toQueryString(route.query.email);

    const queryCode = toQueryString(route.query.code);
    const queryPin = toQueryString(route.query.pin);
    if (queryPin) {
      pin.value = queryPin;
    }

    if (!scannedUrl.value) {
      if (AuthService.currentUser) {
        // Logged-in users landing here with nothing to claim belong on the dashboard
        Router.replace({ name: "Dashboard" });
        return;
      }

      // Step 1: No code in URL, show container input (pre-filled if a code was passed)
      containerID.value = queryCode.toUpperCase().trim();
      logger.info("No code in URL");
      return;
    }

    if (AuthService.currentUser) {
      // Logged-in users don't need the manual claim UI, submit right away
      const claimResponse = await APIService.claimCodeByUrl(scannedUrl.value, {
        source: "qr-scan",
        platform: isMobile() ? "mobile web" : "web",
        codeForm: "dynamic-link",
      });
      handleClaimResponse(claimResponse);
      return;
    }

    // Step 3: Code present in URL, skip to container loading
    logger.info("Loading container from URL code:", scannedUrl.value);

    supportsQuickMoney.value = await APIService.allowQuickClaim(scannedUrl.value);
    if (!supportsQuickMoney.value) {
      // No quick money to offer, but the user isn't logged in yet, so we still need their email before claiming
      showFastlaneClaim.value = true;
      return;
    }
  } catch (err) {
    logger.error("Error loading container from URL:", err);
    if (scannedUrl.value && !AuthService.currentUser) {
      // fall back to the fastlane claim since all it needs is the scanned URL
      showFastlaneClaim.value = true;
    }
  } finally {
    loading.value = false;
    window.history.replaceState({}, document.title, "/return");
  }
});

const details = computed(() => [
  {
    title: t("quickReturn.panelDetails.step1.title"),
    image: LineContainersCloud,
    text: t("quickReturn.panelDetails.step1.text"),
    // buttonLink: process.env.VUE_APP_HOSTING_URL + "/completeSignup",
    // buttonText: "Complete Account",
  },
  {
    title: t("quickReturn.panelDetails.step2.title"),
    image: LineDropOffBinCloud,
    text: t("quickReturn.panelDetails.step2.text"),
    // buttonLink: t("quickReturn.panelDetails.step2.buttonURL"),
    // buttonText: t("quickReturn.panelDetails.step2.buttonText"),
  },
  {
    title: t("quickReturn.panelDetails.step3.title"),
    image: MoneyWithArrowCloud,
    text: t("quickReturn.panelDetails.step3.text"),
  },
  {
    title: "save",
    image: LineEarthCloud,
    text: "Thank you for being a part of the solution! A small switch to reusables makes a huge difference in the world.",
  },
]);

watch(success, () => {
  if (!success.value) return;
  // Clear form
  containerID.value = "";
  userEmail.value = "";
  emailSuggestion.value = null;
  showFastlaneClaim.value = false;
});

function containerChange(e) {
  containerID.value = e.target.value.toUpperCase().trim();
  e.target.value = containerID.value;
}

function emailChange(e) {
  emailSuggestion.value = null;
  userEmail.value = e.target.value.toLowerCase().trim();
  e.target.value = userEmail.value;
}

function emailBlur() {
  emailSuggestion.value = getSuggestedEmail(userEmail.value);
}

function applyEmailSuggestion() {
  if (!emailSuggestion.value) return;
  userEmail.value = emailSuggestion.value;
  emailSuggestion.value = null;
}

async function handleQuickDonate() {
  // Navigate to charity selection page
  const automatic = !!scannedUrl.value;
  Router.replace({
    name: "CharitySelection",
    query: {
      codeDisplay: containerID.value,
      codeUrl: scannedUrl.value,
      codeForm: automatic ? "dynamic-link" : "pure-code",
      platform: isMobile() ? "mobile web" : "web",
      source: "quick-donate",
      email: userEmail.value || undefined,
      pin: pin.value || undefined,
    },
  });
}

async function handleQuickMoney() {
  if (!userEmail.value) {
    error.value = t("claimContainer.error.enter-email");
    return;
  }

  // Show T&Cs modal
  notifyStore.createModal(
    t("quickReturn.quickMoney.alertTitle"),
    t("quickReturn.quickMoney.alertText"),
    [
      {
        id: "confirm",
        text: t("quickReturn.quickMoney.alertProceed"),
        callback: async () => {
          claimLoading.value = true;

          const language = navigator.language || navigator.userLanguage || "en";
          const claimResponse = await APIService.claimCodeByUrlQuickClaim(
            scannedUrl.value,
            userEmail.value,
            language,
            undefined,
            {
              platform: isMobile() ? "mobile web" : "web",
              source: "quick-money",
              codeForm: "dynamic-link",
            },
          );

          handleClaimResponse(claimResponse);
        },
        action: "close",
      },
      {
        id: "cancel",
        text: t("quickReturn.quickMoney.alertCancel"),
        action: "close",
      },
    ],
    true,
  );
}

async function handleFastlaneClaim() {
  if (!userEmail.value) {
    error.value = t("claimContainer.error.enter-email");
    return;
  }

  claimLoading.value = true;
  error.value = "";

  const claimResponse = await APIService.claimCodeByUrlFastlane(scannedUrl.value, userEmail.value, {
    source: "qr-scan",
    platform: isMobile() ? "mobile web" : "web",
    codeForm: "dynamic-link",
  });
  logger.info("Fastlane claim response:", claimResponse);
  handleClaimResponse(claimResponse);
}

async function handleStandardClaim() {
  if (containerID.value == "" || userEmail.value == "") {
    error.value = "Please make sure a container code and email were entered";
    return;
  }
  claimLoading.value = true;
  error.value = "";

  let claimResponse = await APIService.claimCode(
    containerID.value,
    userEmail.value,
    {
      source: "manual",
      platform: isMobile() ? "mobile web" : "web",
      codeForm: "pure-code",
    },
    pin.value ? pin.value : undefined,
    null,
    true, // isUnauthenticated claim
  );
  handleClaimResponse(claimResponse);
}

const handleClaimResponse = (claimResponse) => {
  if (claimResponse == null || claimResponse == undefined) {
    error.value = "Failed to claim container";
    claimLoading.value = false;
    return;
  }

  if (claimResponse.error) {
    switch (claimResponse.code) {
      case 30000:
      case 30002:
      case 30003:
      case 30019:
        error.value = "Failed to claim container, try again later";
        break;
      case 30004:
        logger.error(`ClaimContainer.HandleClaim: Container ${containerID.value} has already been claimed.`);
        notifyStore.createModal(
          t("claimContainer.error.notice"),
          t("claimContainer.error.already-claimed-modal"),
          [
            {
              id: "close",
              text: t("claimContainer.error.dismiss"),
              action: "close",
            },
          ],
          true,
        );
        break;
      case 20004:
        knownLoginNotify();
        break;
      default:
        unknownError();
        break;
    }
  }

  if (!claimResponse.error) {
    success.value = true;
  }
  claimLoading.value = false;
};

const unknownError = () => {
  notifyStore.createModal(
    t("claimContainer.error.title"),
    t("claimContainer.error.generic"),
    [
      {
        id: "close",
        text: t("claimContainer.error.dismiss"),
        action: "close",
      },
    ],
    true,
  );
};

const knownLoginNotify = () => {
  notifyStore.createModal(
    t("claimContainer.error.alert"),
    t("claimContainer.error.anonymous-limit"),
    [
      {
        id: "login",
        text: t("claimContainer.error.login"),
        callback: () => {
          Router.replace("/");
        },
        action: "close",
      },
      {
        id: "close",
        text: t("claimContainer.error.dismiss"),
        action: "close",
      },
    ],
    true,
  );
};
</script>

<style scoped lang="scss">
@import "@/revamp/assets/scss/colors.scss";
@import "@/revamp/assets/scss/elements.scss";
@import "@/revamp/assets/scss/variables.scss";

.container-input-section {
  width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;
  margin-top: 15px;

  .title {
    font-size: 1.1rem;
    color: $sunflower;
    width: 100%;
    margin-bottom: 15px;
    text-align: center;
  }

  .label {
    flex: 0 0 75px;
    margin-right: 10px;
    margin-bottom: 0;
    color: $sunflower;
  }

  .input-container {
    display: flex;
    align-items: center;
    margin-top: 15px;
    justify-content: center;

    .input {
      width: 100%;
      border-radius: 10px;
      padding: 10px;
      border: 1px solid $cloud;
      font-size: 1rem;
      align-items: center;
      text-align: center;
    }
  }

  .input-with-icon {
    position: relative;
    display: flex;
    align-items: center;
    margin-top: 15px;
    justify-content: center;

    .input {
      width: 100%;
      border-radius: 10px;
      padding: 10px;
      border: 1px solid $cloud;
      font-size: 1rem;
      align-items: center;
      text-align: center;
    }

    .info-icon {
      position: absolute;
      right: 8px;
      /* adjust as needed */
      top: 50%;
      transform: translateY(-50%);
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background-color: $denim;
      color: $cloud;
      font-weight: bold;
      text-align: center;
      line-height: 18px;
      font-size: 12px;
      cursor: pointer;
      user-select: none;
    }
  }

  .button-container {
    margin-top: 15px;
    text-align: center;

    .button {
      padding: 12px 24px;
      border-radius: 10px;
      border: none;
      font-weight: bold;
      font-size: 1rem;
      cursor: pointer;
      transition: all 0.3s ease;

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }

    .primary-button {
      background-color: $sunflower;
      color: $denim;

      &:hover:not(:disabled) {
        background-color: darken($sunflower, 10%);
      }
    }
  }
}

.action-buttons-section {
  width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;
  margin-top: 15px;

  .title {
    font-size: 1.1rem;
    color: $sunflower;
    width: 100%;
    margin-bottom: 15px;
    text-align: center;
  }

  .email-input-container {
    display: flex;
    align-items: flex-start;
    margin-top: 15px;
    margin-bottom: 20px;

    .label {
      flex: 0 0 75px;
      margin-right: 10px;
      margin-bottom: 0;
      margin-top: 10px;
      color: $sunflower;
    }

    .input-wrapper {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-width: 0;
    }

    .input {
      width: 100%;
      border-radius: 10px;
      padding: 10px;
      border: 1px solid $cloud;
      font-size: 1rem;
      align-items: center;
      text-align: center;
    }

    .email-suggestion {
      margin-left: 0;
      margin-top: 8px;
      color: $sunflower;
    }

    .email-suggestion-link {
      appearance: none;
      border: none;
      background: none;
      color: #0048b4;
      font: inherit;
      padding: 0;
      text-decoration: underline;
      cursor: pointer;
    }
  }
}

.pin-tooltip {
  background-color: $denim;
  color: $cloud;
  border-radius: 8px;
  font-size: 0.85rem;
  line-height: 1.4;
  text-align: center;
  width: 100%;
}

.action-buttons-section {
  .button-container {
    margin-top: 15px;

    .button {
      width: 100%;
      padding: 15px;
      border-radius: 10px;
      border: none;
      font-weight: bold;
      font-size: 1.1rem;
      cursor: pointer;
      transition: all 0.3s ease;

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }

    .primary-button {
      background-color: $sunflower;
      color: $denim;

      &:hover:not(:disabled) {
        background-color: darken($sunflower, 10%);
      }
    }

    .secondary-button {
      background-color: $ocean;
      color: $denim;

      &:hover {
        background-color: darken($ocean, 10%);
      }
    }
  }
}

.success-section {
  text-align: center;
  margin-top: 20px;

  .success-title {
    font-size: 1.35rem;
    font-weight: bold;
    line-height: 1.3;
    margin: 0 0 14px;
  }

  .success-detail {
    font-size: 1.05rem;
    font-weight: bold;
    line-height: 1.4;
    margin: 0 auto;
    max-width: 290px;
  }
}

.error {
  color: red;
  width: 100%;
  margin-top: 15px;
  text-align: center;
}

.logo {
  max-width: 30%;
  min-width: 100%;
}

.image-spacer {
  width: 100%;
  height: 200px;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;

  &.extra-space {
    height: 350px;
    background-color: #d3d3d3;
  }
}

.title {
  color: $sunflower;
}

.detail-card {
  margin-top: 10px;

  div {
    font-size: 2rem;
    color: $cloud;
    width: 100%;
    text-align: center;
    margin-bottom: 10px;
  }

  img {
    max-height: 125px;
    max-width: 150px;
    margin: 5px auto;
    text-align: center;
  }

  p {
    color: $cloud;
    margin: 0px 30px !important;
    margin-bottom: 20px !important;
    text-align: center;
  }

  span.divider {
    background-color: $cloud;
    height: 1px;
    width: 80%;
    display: block;
    margin: 0 10%;
    margin-top: 20px;
  }
}

.sign-off {
  color: $cloud;
  margin: 0px 30px;
  text-align: center;
  margin-top: 20px;
}

.button-row {
  width: 100%;
  display: flex;
  justify-content: center;
  margin: 20px 0px;

  .big-button {
    background-color: $cloud;
    color: $denim;
    text-align: center;
  }
}

.foot {
  display: flex;
  align-items: center;
  justify-content: center;

  a {
    text-decoration: none;
    color: inherit;
    padding: 0;
    margin: 0;
    display: inline-block;
    text-align: center;
    padding: 5px;
    width: fit-content;
    max-width: 100px;

    span {
      text-decoration: underline;
      color: $cloud;
      padding: 0;
      margin: 0;
      display: inline-block;
    }
  }

  .divider {
    padding: 0;
    margin: 0;
    color: $cloud;
    width: 7%;
    display: inline-block;
  }
}

.copyright {
  color: $cloud;
  padding-top: 10px;
  text-align: center;
}
</style>
